import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Based on https://github.com/MrLightful/shadcn-tree-view
 *
 * This version is refactored to use a checkbox for each node, and a checkbox for the parent node
 * when the children are partially selected.
 */
import React, { useState, useMemo, useCallback, useEffect, memo } from "react";
import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { ChevronRight, Square, SquareCheckBig, SquareMinus } from "lucide-react";
import { cva } from "class-variance-authority";
import { cn } from "../../lib/utils";
const treeVariants = cva("group hover:before:opacity-100 before:absolute before:rounded-lg before:left-0 px-2 before:w-full before:opacity-0 before:bg-accent/70 before:h-8 before:-z-10", {
    variants: {
        selected: {
            true: "before:opacity-100 before:bg-accent/70 text-accent-foreground",
            false: null,
        },
        disabled: {
            true: "pointer-events-none opacity-50",
            false: null,
        },
        checked: {
            true: "text-yellow-700",
            false: null,
        },
    },
    defaultVariants: {
        selected: false,
        disabled: false,
        checked: false,
    },
});
const getSelectionState = (item, selectedItems) => {
    if (!item.children || item.children.length === 0) {
        return selectedItems.has(item.id)
            ? TreeNodeSelectionState.Full
            : TreeNodeSelectionState.None;
    }
    let fullCount = 0;
    let noneCount = 0;
    const totalChildren = item.children.length;
    for (let i = 0; i < totalChildren; i++) {
        const child = item.children[i];
        const childState = getSelectionState(child, selectedItems);
        if (childState === TreeNodeSelectionState.Partial) {
            return TreeNodeSelectionState.Partial;
        }
        if (childState === TreeNodeSelectionState.Full) {
            fullCount++;
        }
        else {
            noneCount++;
        }
        if (fullCount > 0 && noneCount > 0) {
            return TreeNodeSelectionState.Partial;
        }
    }
    if (fullCount === totalChildren)
        return TreeNodeSelectionState.Full;
    if (noneCount === totalChildren)
        return TreeNodeSelectionState.None;
    return TreeNodeSelectionState.Partial;
};
export var TreeItemStatus;
(function (TreeItemStatus) {
    TreeItemStatus["Disabled"] = "disabled";
    TreeItemStatus["Enabled"] = "enabled";
})(TreeItemStatus || (TreeItemStatus = {}));
var TreeNodeSelectionState;
(function (TreeNodeSelectionState) {
    TreeNodeSelectionState["None"] = "none";
    TreeNodeSelectionState["Partial"] = "partial";
    TreeNodeSelectionState["Full"] = "full";
})(TreeNodeSelectionState || (TreeNodeSelectionState = {}));
const TreeContext = React.createContext({ flattenedItems: {}, checkedItems: new Set() });
export const getParentsMap = (items) => {
    const parentsMap = {};
    const traverse = (items, parents = []) => {
        items.forEach((item) => {
            parentsMap[item.id] = [...parents];
            if (item.children) {
                traverse(item.children, [...parents, item]);
            }
        });
    };
    traverse(items);
    return parentsMap;
};
export const processTree = (items) => {
    const parentsMap = {};
    const flattenedItems = {};
    const parentPath = [];
    const traverse = (items) => {
        const itemsLength = items.length;
        for (let i = 0; i < itemsLength; i++) {
            const item = items[i];
            const itemId = item.id;
            parentsMap[itemId] = parentPath.slice(); // use slice for better performance
            flattenedItems[itemId] = item;
            const children = item.children;
            if (children && children.length > 0) {
                parentPath.push(item);
                traverse(children);
                parentPath.pop();
            }
        }
    };
    traverse(items);
    return { parentsMap, flattenedItems };
};
/**
 * Ensures child items are selected when their parent is selected.
 *
 * This function takes a tree structure and a set of selected item IDs, then
 * adds any child items to the selection if their parent is already selected.
 * This maintains selection consistency in hierarchical data.
 *
 * @param items - The tree data structure to process
 * @param selectedItems - The current set of selected item IDs
 * @param parentsMap - A mapping of item IDs to their parent items
 * @returns A new Set containing the original selections plus any child items of selected parents
 */
const fixSelectedItems = (items, selectedItems) => {
    if (selectedItems.size === 0) {
        return new Set();
    }
    const fixedSelectedItems = new Set(selectedItems);
    // Create a mapping that maps each item ID to its direct children
    const directChildrenMap = new Map();
    // Preprocess all items to build the direct children mapping
    const buildDirectChildrenMap = (items) => {
        for (let i = 0; i < items.length; i++) {
            const item = items[i];
            if (item.children && item.children.length > 0) {
                directChildrenMap.set(item.id, item.children.map((child) => child.id));
                buildDirectChildrenMap(item.children);
            }
        }
    };
    buildDirectChildrenMap(items);
    // Use BFS to process all selected items
    const selectedArray = Array.from(selectedItems);
    for (let i = 0; i < selectedArray.length; i++) {
        const id = selectedArray[i];
        const children = directChildrenMap.get(id);
        if (children) {
            for (let j = 0; j < children.length; j++) {
                const childId = children[j];
                if (!fixedSelectedItems.has(childId)) {
                    fixedSelectedItems.add(childId);
                    selectedArray.push(childId); // Add newly discovered child items to the processing queue
                }
            }
        }
    }
    return fixedSelectedItems;
};
const filterTree = (items, filter) => {
    if (!filter || filter === "")
        return items;
    const lowerFilter = filter.toLowerCase();
    const resultCache = new Map();
    const matchesFilter = (item) => {
        const cachedResult = resultCache.get(item.id);
        if (cachedResult !== undefined) {
            return cachedResult;
        }
        const matches = item.name.toLowerCase().includes(lowerFilter);
        resultCache.set(item.id, matches);
        return matches;
    };
    const result = [];
    const itemsLength = items.length;
    for (let i = 0; i < itemsLength; i++) {
        const item = items[i];
        if (matchesFilter(item)) {
            result.push(item);
            continue;
        }
        if (item.children && item.children.length > 0) {
            const filteredChildren = filterTree(item.children, filter);
            if (filteredChildren.length > 0) {
                result.push({ ...item, children: filteredChildren });
            }
        }
    }
    return result;
};
const TreeView = React.forwardRef(({ data, preCalcInfo, initialFocusedItemId, expandAll, defaultSelectedIds, defaultLeafIcon, defaultNodeIcon, iconMap, disabled, filter = "", errorMessage, onFocusChange, onSelectChange, className, ...props }, ref) => {
    const [initiated, setInitiated] = useState(false);
    const [focusedItemId, setFocusedItemId] = useState(initialFocusedItemId);
    const [selectedItems, setSelectedItems] = useState(defaultSelectedIds || new Set());
    const [displaySelectedItems, setDisplaySelectedItems] = useState(defaultSelectedIds || new Set());
    const filteredData = useMemo(() => {
        const items = Array.isArray(data) ? data : [data];
        const result = filterTree(items, filter);
        return result;
    }, [data, filter]);
    const { parentsMap, flattenedItems } = useMemo(() => {
        if (preCalcInfo)
            return preCalcInfo;
        const items = Array.isArray(data) ? data : [data];
        return processTree(items);
    }, [data, preCalcInfo]);
    useEffect(() => {
        if (initiated)
            return;
        // Since the received selectedItems might only contain parent nodes without their children,
        // but we need to display all children nodes as checked as well,
        // we need to adjust the selectedItems to include all relevant children
        const fixedSelectedItems = fixSelectedItems(filteredData, displaySelectedItems);
        setDisplaySelectedItems(fixedSelectedItems);
        setInitiated(true);
    }, [displaySelectedItems, filteredData, initiated]);
    const handleFocusChange = useCallback((item) => {
        setFocusedItemId(item?.id);
        if (onFocusChange) {
            Promise.resolve().then(() => onFocusChange(item));
        }
    }, [onFocusChange]);
    /**
     * Handles checkbox selection/deselection for tree nodes
     *
     * This function manages two separate selection states:
     * 1. displaySelectedItems - Visual representation of selection (includes auto-selected items)
     * 2. selectedItems - Tracks actual user selections (what the user explicitly clicked)
     *
     * @param itemId - ID of the tree node being toggled
     * @param selected - Whether the node is being selected (true) or deselected (false)
     */
    const handleCheckboxChange = useCallback((itemId, selected) => {
        const item = flattenedItems[itemId];
        if (!item)
            return;
        const children = item.children;
        // PART 1: Update displaySelectedItems (visual representation)
        setDisplaySelectedItems((prevDisplaySelectedItems) => {
            const newDisplaySelectedItems = new Set(prevDisplaySelectedItems);
            const setItem = (id, select) => {
                return select
                    ? newDisplaySelectedItems.add(id)
                    : newDisplaySelectedItems.delete(id);
            };
            // When deselecting a node, remove all parent nodes from display selection
            if (!selected) {
                const parents = parentsMap[itemId];
                parents.forEach((parent) => {
                    newDisplaySelectedItems.delete(parent.id);
                });
            }
            // Update the clicked item's selection state
            setItem(itemId, selected);
            // Propagate selection to all children (cascade effect)
            if (children) {
                const toggleChildren = (items, select) => {
                    items.forEach((child) => {
                        setItem(child.id, select);
                        if (child.children) {
                            toggleChildren(child.children, select);
                        }
                    });
                };
                toggleChildren(children, selected);
            }
            return newDisplaySelectedItems;
        });
        // PART 2: Update selectedItems (actual user selections)
        setSelectedItems((prevSelectedItems) => {
            const newSelectedItems = new Set(prevSelectedItems);
            const setItem = (id, select) => {
                return select
                    ? newSelectedItems.add(id)
                    : newSelectedItems.delete(id);
            };
            if (!selected) {
                // When deselecting: remove all parent nodes from actual selection
                const parents = parentsMap[itemId];
                parents.forEach((parent) => {
                    const isSelected = newSelectedItems.delete(parent.id);
                    if (onSelectChange) {
                        Promise.resolve().then(() => onSelectChange(parent.id, false));
                    }
                    // If a parent was explicitly selected before:
                    // Re-select all its children except the one being deselected
                    // This preserves the selection state of siblings when deselecting one child
                    if (isSelected && parent.children) {
                        parent.children.forEach((child) => {
                            if (child.id !== itemId) {
                                newSelectedItems.add(child.id);
                                if (onSelectChange) {
                                    Promise.resolve().then(() => onSelectChange(child.id, true));
                                }
                            }
                        });
                    }
                });
            }
            // When deselecting a parent: remove all intermediate nodes (non-leaf nodes)
            // This ensures that only explicitly selected items remain in the selection
            if (children) {
                const cancelChildren = (items) => {
                    items.forEach((child) => {
                        if (!child.children)
                            return;
                        if (setItem(child.id, false)) {
                            onSelectChange?.(child.id, false);
                        }
                        cancelChildren(child.children);
                    });
                };
                cancelChildren(children);
            }
            // Finally, update the clicked item's actual selection state
            setItem(itemId, selected);
            if (onSelectChange) {
                Promise.resolve().then(() => onSelectChange(itemId, selected));
            }
            return newSelectedItems;
        });
    }, [flattenedItems, parentsMap, onSelectChange]);
    const expandedItemIds = useMemo(() => {
        if (!initialFocusedItemId) {
            return [];
        }
        const ids = [];
        function walkTreeItems(items, targetId) {
            if (items instanceof Array) {
                for (let i = 0; i < items.length; i++) {
                    ids.push(items[i].id);
                    if (walkTreeItems(items[i], targetId) && !expandAll) {
                        return true;
                    }
                    if (!expandAll)
                        ids.pop();
                }
            }
            else if (!expandAll && items.id === targetId) {
                return true;
            }
            else if (items.children) {
                return walkTreeItems(items.children, targetId);
            }
        }
        walkTreeItems(data, initialFocusedItemId);
        return ids;
    }, [data, expandAll, initialFocusedItemId]);
    return (_jsx(TreeContext.Provider, { value: {
            defaultNodeIcon,
            defaultLeafIcon,
            iconMap,
            flattenedItems,
            checkedItems: selectedItems,
            disabled,
        }, children: _jsx("div", { className: cn("relative p-2", className), children: filteredData.length > 0 ? (_jsx(TreeItem, { data: filteredData, ref: ref, focusedItemId: focusedItemId, expandedItemIds: expandedItemIds, selectedItems: displaySelectedItems, handleFocusChange: handleFocusChange, handleCheckboxChange: handleCheckboxChange, ...props })) : (_jsx("div", { className: "flex items-center justify-center py-2 px-4", children: _jsx("span", { className: cn("text-sm text-muted-foreground", errorMessage && "text-red-500"), children: errorMessage || "No results found" }) })) }) }));
});
TreeView.displayName = "TreeView";
const TreeItem = React.forwardRef(({ className, data, focusedItemId, expandedItemIds, selectedItems, handleFocusChange, handleCheckboxChange, ...props }, ref) => {
    if (!(data instanceof Array)) {
        data = [data];
    }
    return (_jsx("div", { ref: ref, role: "tree", className: className, ...props, children: _jsx("ul", { children: data.map((item) => (_jsx("li", { children: item.children && item.children.length > 0 ? (_jsx(TreeNode, { item: item, focusedItemId: focusedItemId, expandedItemIds: expandedItemIds, selectedItems: selectedItems, status: item.status, handleFocusChange: handleFocusChange, handleCheckboxChange: handleCheckboxChange })) : (_jsx(TreeLeaf, { item: item, focusedItemId: focusedItemId, selectedItems: selectedItems, status: item.status, handleFocusChange: handleFocusChange, handleCheckboxChange: handleCheckboxChange })) }, item.id))) }) }));
});
TreeItem.displayName = "TreeItem";
const TreeNode = memo(({ item, expandedItemIds, focusedItemId, selectedItems, status, handleFocusChange, handleCheckboxChange, }) => {
    const [value, setValue] = useState(expandedItemIds.includes(item.id) ? [item.id] : []);
    const { flattenedItems, checkedItems, disabled } = React.useContext(TreeContext);
    const rawItem = flattenedItems[item.id];
    const selectionState = getSelectionState(rawItem, selectedItems);
    const renderCheckbox = () => {
        switch (selectionState) {
            case TreeNodeSelectionState.Full:
                return _jsx(SquareCheckBig, { className: "h-4 w-4" });
            case TreeNodeSelectionState.Partial:
                return _jsx(SquareMinus, { className: "h-4 w-4" });
            default:
                return _jsx(Square, { className: "h-4 w-4" });
        }
    };
    const isFocused = focusedItemId === item.id;
    const isDisabled = status === TreeItemStatus.Disabled || disabled;
    return (_jsx(AccordionPrimitive.Root, { type: "multiple", value: value, onValueChange: (s) => setValue(s), children: _jsxs(AccordionPrimitive.Item, { value: item.id, children: [_jsxs(AccordionTrigger, { className: cn(treeVariants({
                        selected: isFocused,
                        checked: checkedItems.has(item.id),
                    })), onClick: () => {
                        handleFocusChange(item);
                        item.onClick?.();
                    }, children: [_jsx("div", { onClick: (e) => {
                                if (isDisabled)
                                    return;
                                e.stopPropagation();
                                handleCheckboxChange(item.id, !selectedItems.has(item.id));
                            }, className: cn("mr-2", isDisabled
                                ? "cursor-not-allowed text-muted-foreground"
                                : "cursor-pointer"), children: renderCheckbox() }), _jsx(TreeIcon, { item: item, isFocused: isFocused, isOpen: value.includes(item.id) }), _jsx("span", { className: "text-sm truncate", children: item.name }), isFocused && _jsx(TreeActions, { children: item.actions })] }), _jsx(AccordionContent, { className: "ml-4 pl-1 border-l", children: _jsx(TreeItem, { data: item.children ? item.children : item, focusedItemId: focusedItemId, expandedItemIds: expandedItemIds, selectedItems: selectedItems, handleFocusChange: handleFocusChange, handleCheckboxChange: handleCheckboxChange }) })] }) }));
});
const TreeLeaf = React.forwardRef(({ className, item, focusedItemId, selectedItems, status, handleFocusChange, handleCheckboxChange, ...props }, ref) => {
    const { disabled, checkedItems } = React.useContext(TreeContext);
    const isSelected = selectedItems.has(item.id);
    const isFocused = focusedItemId === item.id;
    const isDisabled = status === TreeItemStatus.Disabled || disabled;
    return (_jsxs("div", { ref: ref, className: cn("ml-5 flex text-left items-center py-2 before:right-1", treeVariants({
            selected: isFocused,
            checked: checkedItems.has(item.id),
        }), className), onClick: () => {
            handleFocusChange(item);
            item.onClick?.();
        }, ...props, children: [_jsx("div", { onClick: (e) => {
                    if (isDisabled)
                        return;
                    e.stopPropagation();
                    handleCheckboxChange(item.id, !isSelected);
                }, className: cn("mr-2", isDisabled
                    ? "cursor-not-allowed text-muted-foreground"
                    : "cursor-pointer"), children: isSelected ? (_jsx(SquareCheckBig, { className: "h-4 w-4" })) : (_jsx(Square, { className: "h-4 w-4" })) }), _jsx(TreeIcon, { item: item, isFocused: isFocused }), _jsx("span", { className: "grow text-sm truncate", children: item.name }), isFocused && _jsx(TreeActions, { children: item.actions })] }));
});
TreeLeaf.displayName = "TreeLeaf";
const AccordionTrigger = React.forwardRef(({ className, children, ...props }, ref) => (_jsx(AccordionPrimitive.Header, { children: _jsxs(AccordionPrimitive.Trigger, { ref: ref, className: cn("flex flex-1 w-full items-center py-2 transition-all [&[data-state=open]>svg]:first:rotate-90", className), ...props, children: [_jsx(ChevronRight, { className: "h-4 w-4 shrink-0 transition-transform duration-200 text-accent-foreground/50 mr-1" }), children] }) })));
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;
const AccordionContent = React.forwardRef(({ className, children, ...props }, ref) => (_jsx(AccordionPrimitive.Content, { ref: ref, className: cn("overflow-hidden text-sm transition-all data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down", className), ...props, children: _jsx("div", { className: "pb-1 pt-0", children: children }) })));
AccordionContent.displayName = AccordionPrimitive.Content.displayName;
const TreeIcon = memo(({ item, isOpen, isFocused, }) => {
    const { defaultNodeIcon, defaultLeafIcon, iconMap } = React.useContext(TreeContext);
    let Icon = item.children ? defaultNodeIcon : defaultLeafIcon;
    if (isFocused && item.focusedIcon) {
        Icon = item.focusedIcon;
    }
    else if (isOpen && item.openIcon) {
        Icon = item.openIcon;
    }
    else if (item.type && iconMap && iconMap[item.type]) {
        Icon = iconMap[item.type];
    }
    else if (item.icon) {
        Icon = item.icon;
    }
    return Icon ? _jsx(Icon, { className: "h-4 w-4 shrink-0 mr-2" }) : _jsx(_Fragment, {});
});
const TreeActions = memo(({ children }) => {
    return _jsx("div", { className: "absolute right-3 group-hover:block", children: children });
});
export { TreeView };
//# sourceMappingURL=tree.js.map